import React from "react";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Edit,
  FileText,
} from "lucide-react";

import type { CourtRetentionSettings } from "./RetentionSetting.types";

interface RetentionSettingsTableProps {
  settings: CourtRetentionSettings[];
  onEdit: (id: string) => void;
}

const RetentionSettingsTable: React.FC<RetentionSettingsTableProps> = ({
  settings,
  onEdit,
}) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
      <h2 className="text-xl font-semibold text-gray-800">
        Retention setting
      </h2>
    </div>

    <div className="overflow-x-auto">
      <table className="md:table w-full">
        <thead className="bg-gray-100">
          <tr className="border border-gray-100">
            <th className="text-left p-2 text-gray-800 font-medium">S. No</th>
            <th className="text-left p-2 text-gray-800 font-medium">Court</th>
            <th className="text-left p-2 text-gray-800 font-medium">
              Retention
            </th>
            <th className="text-left p-2 text-gray-800 font-medium">
              Archived At
            </th>
            <th className="text-left p-2 text-gray-800 font-medium">
              Status
            </th>
            <th className="text-left p-2 text-gray-800 font-medium">
              Description
            </th>
            <th className="text-left p-2 text-gray-800 font-medium">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {settings.length > 0 ? (
            settings.map((setting, index) => (
              <tr
                key={setting._id}
                className={`border-b border-gray-100 transition-colors ${
                  index % 2 === 0 ? "bg-white" : "bg-blue-50"
                } hover:bg-gray-50`}
              >
                <td className="p-2 text-gray-700 font-normal">{index + 1}</td>
                <td className="p-2 text-gray-700 font-normal">
                  {setting.courtType}
                </td>
                <td className="p-2 text-gray-700 font-normal">
                  <div className="inline-flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span>{setting.retentionDays} days</span>
                  </div>
                </td>
                <td className="p-2 text-gray-700 font-normal">
                  {setting.retentionTimeUnit || "-"}
                </td>
                <td className="p-2 text-gray-700 font-normal">
                  <RetentionStatus isActive={setting.isActive} />
                </td>
                <td className="p-2 text-gray-700 font-normal">
                  <div className="max-w-xs truncate">
                    {setting.description || "-"}
                  </div>
                </td>
                <td className="p-2">
                  <button
                    onClick={() => onEdit(setting._id)}
                    className="text-blue-500 hover:text-gray-800 rounded-lg hover:bg-white/10 transition-colors"
                    title="Edit retention setting"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={7}
                className="px-6 py-16 text-center text-sm text-gray-500"
              >
                <div className="flex flex-col items-center justify-center space-y-3">
                  <FileText className="h-12 w-12 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-600 mb-0">
                    No Retention Settings Found
                  </h3>
                  <p className="text-xs text-gray-400">
                    Add retention settings for High Court and District Court
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

interface RetentionStatusProps {
  isActive: boolean;
}

const RetentionStatus: React.FC<RetentionStatusProps> = ({ isActive }) => (
  <div
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
      isActive
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-slate-100 text-slate-600 border-slate-200"
    }`}
  >
    {isActive ? (
      <CheckCircle2 className="w-3.5 h-3.5" />
    ) : (
      <AlertCircle className="w-3.5 h-3.5" />
    )}
    {isActive ? "Active" : "Inactive"}
  </div>
);

export default RetentionSettingsTable;
